/**
 * FieldRenderer — dispatcher that maps a FieldDef.type to the correct
 * primitive or container component.
 *
 * This is the single place that knows about all primitive types. Adding a new
 * type means adding one case here.
 */

import type { ReactNode } from 'react';
import type { FieldDef, RendererProps } from './types';
import { ConditionalR } from './containers/ConditionalR';
import { RepeaterR } from './containers/RepeaterR';
import { TextFieldR } from './primitives/TextFieldR';
import { YesNoR } from './primitives/YesNoR';
import { DateR } from './primitives/DateR';
import { EnumR } from './primitives/EnumR';
import { SubdivisionR } from './primitives/SubdivisionR';
import { MultiEnumR } from './primitives/MultiEnumR';
import { AddressR } from './primitives/AddressR';
import { NameBlockR } from './primitives/NameBlockR';
import { NumberR } from './primitives/NumberR';
import { PhoneR } from './primitives/PhoneR';
import { EmailR } from './primitives/EmailR';
import { PassportNumberR } from './primitives/PassportNumberR';
import { CurrencyAmountR } from './primitives/CurrencyAmountR';
import { LongTextR } from './primitives/LongTextR';
import { FileUploadR } from './primitives/FileUploadR';
import { ReadOnlyValueR } from './primitives/ReadOnlyValueR';
import { InfoR } from './primitives/InfoR';

interface Props extends RendererProps {
  field: FieldDef;
}

/**
 * Render a single field from a schema FieldDef.
 * Wraps in ConditionalR when appears_when or conditional_on is present.
 */
export function FieldRenderer({ field, formId, pdfMode = false, namePrefix, audience }: Props): ReactNode {
  const condition = field.appears_when ?? field.conditional_on;
  const fieldNode = renderFieldByType({ field, formId, pdfMode, namePrefix, audience });

  if (condition) {
    return (
      <ConditionalR expression={condition} entryPrefix={namePrefix}>
        {fieldNode}
      </ConditionalR>
    );
  }

  return fieldNode;
}

// ---------------------------------------------------------------------------
// Type dispatcher
// ---------------------------------------------------------------------------

function renderFieldByType({ field, formId, pdfMode, namePrefix, audience }: Props): ReactNode {
  const sharedProps = { field, formId, pdfMode, namePrefix, audience };

  // System-managed value: rendered as a locked display, never an input (e.g.
  // relationship status, which only the matter Overview basis switch may
  // change so the matter and the form can never disagree). pdfMode keeps its
  // own value rendering via the primitives below.
  if (field.read_only && !pdfMode) {
    return <ReadOnlyValueR {...sharedProps} />;
  }

  try {
    switch (field.type) {
      case 'text':
        return <TextFieldR {...sharedProps} />;

      case 'long_text':
        return <LongTextR {...sharedProps} />;

      case 'yn':
        return <YesNoR {...sharedProps} />;

      case 'date':
      case 'date_partial':
        return <DateR {...sharedProps} />;

      case 'enum':
        return <EnumR {...sharedProps} />;

      case 'subdivision':
        return <SubdivisionR {...sharedProps} />;

      case 'multi_enum':
        return <MultiEnumR {...sharedProps} />;

      case 'number':
        return <NumberR {...sharedProps} />;

      case 'phone':
        return <PhoneR {...sharedProps} />;

      case 'email':
        return <EmailR {...sharedProps} />;

      case 'passport_number':
        return <PassportNumberR {...sharedProps} />;

      case 'currency_amount':
        return <CurrencyAmountR {...sharedProps} />;

      case 'address':
        return <AddressR {...sharedProps} />;

      case 'name_block':
        return <NameBlockR {...sharedProps} />;

      case 'file_upload':
        return <FileUploadR {...sharedProps} />;

      case 'info':
        return <InfoR {...sharedProps} />;

      case 'repeater':
        return (
          <RepeaterR
            {...sharedProps}
            renderEntry={({ entrySchema, namePrefix: entryPrefix, formId: fid, pdfMode: pdf }) => (
              <div className="space-y-4">
                {entrySchema.map((entryField) => (
                  <FieldRenderer
                    key={entryField.canonical_name}
                    field={entryField}
                    formId={fid}
                    pdfMode={pdf}
                    namePrefix={entryPrefix}
                    audience={audience}
                  />
                ))}
              </div>
            )}
          />
        );

      default: {
        const exhaustiveCheck: never = field.type;
        console.warn('[FieldRenderer] Unknown field type:', exhaustiveCheck, field.canonical_name);
        return (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            Unknown field type: {String(field.type)} ({field.canonical_name})
          </div>
        );
      }
    }
  } catch (err) {
    console.error('[FieldRenderer] Error rendering field:', field.canonical_name, err);
    return (
      <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
        Error rendering field: {field.canonical_name}
      </div>
    );
  }
}
